import React from 'react';
import { CircuitRenderer, CircuitElement } from './CircuitRenderer';

interface CapacitorCombinationDiagramProps {
    capacitors: number[];
    equivalent: number;
    isSeries: boolean;
}

export const CapacitorCombinationDiagram: React.FC<CapacitorCombinationDiagramProps> = ({
    capacitors,
    equivalent,
    isSeries
}) => {
    const width = 450;
    const height = 300;
    const padding = 50;

    const elements: CircuitElement[] = [];

    if (isSeries) {
        // Series layout: horizontal line with capacitors in sequence
        // ─┤├─┤├─┤├─
        const topY = height / 2;
        const startX = padding;
        const endX = width - padding;
        const totalWidth = endX - startX;

        // Divide space evenly among capacitors
        const segmentWidth = totalWidth / capacitors.length;

        capacitors.forEach((cap, i) => {
            const segmentStart = startX + i * segmentWidth;
            const segmentEnd = segmentStart + segmentWidth;

            // Wire before capacitor (except for first)
            if (i === 0) {
                elements.push({
                    type: 'wire',
                    from: { x: segmentStart, y: topY },
                    to: { x: segmentStart + segmentWidth * 0.2, y: topY }
                });
            }

            // Capacitor
            elements.push({
                type: 'capacitor',
                from: { x: segmentStart + segmentWidth * 0.2, y: topY },
                to: { x: segmentEnd - segmentWidth * 0.2, y: topY },
                value: cap,
                label: `C${i + 1}=${cap}μF`,
                labelSide: 'left'
            });

            // Wire after capacitor
            if (i < capacitors.length - 1) {
                elements.push({
                    type: 'wire',
                    from: { x: segmentEnd - segmentWidth * 0.2, y: topY },
                    to: { x: segmentEnd + segmentWidth * 0.2, y: topY }
                });
            } else {
                elements.push({
                    type: 'wire',
                    from: { x: segmentEnd - segmentWidth * 0.2, y: topY },
                    to: { x: segmentEnd, y: topY }
                });
            }
        });
    } else {
        // Parallel layout: vertical capacitors between two rails
        const topY = padding + 30;
        const bottomY = height - padding - 30;
        const startX = padding + 20;
        const endX = width - padding - 20;
        const totalWidth = endX - startX;

        // Top and bottom rails
        elements.push({
            type: 'wire',
            from: { x: startX, y: topY },
            to: { x: endX, y: topY }
        });
        elements.push({
            type: 'wire',
            from: { x: startX, y: bottomY },
            to: { x: endX, y: bottomY }
        });

        // Vertical capacitors
        const spacing = totalWidth / (capacitors.length + 1);

        capacitors.forEach((cap, i) => {
            const x = startX + spacing * (i + 1);

            elements.push({
                type: 'capacitor',
                from: { x, y: topY },
                to: { x, y: bottomY },
                value: cap,
                label: `C${i + 1}=${cap}μF`,
                labelSide: i % 2 === 0 ? 'left' : 'right' // Alternate sides to prevent overlap
            });
        });
    }

    // Add equivalent capacitance as a junction label
    const junctions = [
        {
            x: width - padding - 30,
            y: isSeries ? height / 2 + 40 : height - 30,
            label: `Ceq = ${equivalent}μF`,
            labelPosition: 'bottom' as const,
            color: 'transparent'
        }
    ];

    return (
        <CircuitRenderer
            width={width}
            height={height}
            elements={elements}
            junctions={junctions}
        />
    );
};
